import { useCallback } from "react";
import useGetAllShopEmailTemplatesSuspense, {
  useInvalidateAllShopEmailTemplates,
} from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import useUpsertEmailTemplate from "@babackapp/private-api-client/client/use-upsert-email-template";
import useCurrentShopSuspense from "./use-current-shop-suspense";
import { useTemplatesEditorConfig } from "../context/templates-editor-config-context";
import { prepareUpsertRequest } from "../lib/prepare-upsert-request";
import type { TemplateType } from "../types";
import type { EmailTemplateContentDto } from "@babackapp/private-api-client/types";
import type { BlockEditorData } from "@babackapp/blocks-editor/types";

function getNewTemplateTitle(
  existingTemplates: { title: string }[],
  baseTitle?: string,
): string {
  const base = baseTitle || "Nouveau template";

  const matchingTitles = existingTemplates
    .map((t) => t.title)
    .filter((title) => title === base || title.startsWith(`${base} `));

  if (matchingTitles.length === 0) {
    return base;
  }

  const numbers = matchingTitles
    .map((title) => {
      if (title === base) return 1;
      const match = title.match(
        new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} (\\d+)$`),
      );
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((n): n is number => n !== null);

  const maxNumber = Math.max(...numbers);

  return `${base} ${maxNumber + 1}`;
}

export type CreateTemplateOptions = {
  type?: TemplateType;
  title?: string;
  content?: EmailTemplateContentDto;
  subject?: Record<string, unknown>;
  preview?: Record<string, unknown>;
  category?: string;
};

export function useCreateTemplate() {
  const { data: shop } = useCurrentShopSuspense();
  const { domain } = useTemplatesEditorConfig();
  const { data: templates = [] } = useGetAllShopEmailTemplatesSuspense(
    shop.id,
    domain,
  );
  const upsertTemplateMutation = useUpsertEmailTemplate(shop.id);
  const invalidateTemplates = useInvalidateAllShopEmailTemplates(shop.id);

  const createTemplate = useCallback(
    async (options: CreateTemplateOptions = {}) => {
      const { type = "email", title, content, subject, preview, category } =
        options;

      const newTitle = getNewTemplateTitle(templates, title);

      // Strip deprecated defaultLocale from content if present (e.g. from model templates)
      const { defaultLocale: _, ...cleanContent } = (content || {
        beforeBlocks: [],
        afterBlocks: [],
        insideBlocks: [],
      }) as BlockEditorData & { defaultLocale?: string };

      const { body } = prepareUpsertRequest({
        data: {
          title: newTitle,
          type,
          subject: subject || {},
          preview: preview || {},
          content: cleanContent as BlockEditorData,
          assets: { uploads: {} },
          category: category ?? null,
          domain,
        },
      });

      const response = await upsertTemplateMutation.mutateAsync({
        requestBody: body,
      });

      await invalidateTemplates();

      return response.template;
    },
    [templates, shop, upsertTemplateMutation, invalidateTemplates, domain],
  );

  return {
    createTemplate,
    isLoading: upsertTemplateMutation.isPending,
  };
}
